// Getting IN and being someone: the service + transport rows, the identity and
// personal plane, org members and SSO, and the platform tokens a caller
// authenticates with.
// Split out of `../capability-manifest.ts`, which is now the barrel over this
// directory. A GROUP, never a meaning: a capability belongs to whichever group
// its `REST_ROUTES` neighbours are in, so these are absent from the barrel and a
// caller reads `CAPABILITY_MANIFEST`. `./manifest` is the only place they compose,
// and `capability-manifest.test.ts` proves the five are a PARTITION of it.

import { NO_PRINCIPAL, OAUTH_DISCOVERY, OAUTH_GRANT, OAUTH_REGISTRATION } from './reasons';
import { type Capability, gap, mcp, na, yes } from './surface';

export const ACCESS_CAPABILITIES: Readonly<Record<string, Capability>> = {
  // ── service + transport ───────────────────────────────────────────────────
  'service.info': { rest: yes, mcp: NO_PRINCIPAL, ui: NO_PRINCIPAL },
  'service.health': { rest: yes, mcp: NO_PRINCIPAL, ui: NO_PRINCIPAL },
  'service.liveness': { rest: yes, mcp: NO_PRINCIPAL, ui: NO_PRINCIPAL },
  'mcp.rpc': {
    rest: yes,
    mcp: na('this IS the MCP transport; a verb addressing it would be a loop'),
    ui: yes,
  },
  'mcp.stream_refused': {
    rest: yes,
    mcp: na(
      'a transport-level method refusal on the MCP endpoint itself — a verb addressing it ' +
        'would be the same loop `mcp.rpc` names, one layer down',
    ),
    ui: na('an HTTP status a client library reads; there is no screen for it'),
  },
  'mcp.help': {
    rest: na(
      'MCP protocol meta — REST is catalogued by its own generated document, not by a route',
    ),
    mcp: mcp('help'),
    ui: na('the dashboard renders screens, not a verb catalog'),
  },
  // `docs` (#3629) — the recipe store as a TOOL, so the recipes reach a model on
  // every turn (`packages/mcp/src/docs.ts` header). The set is DERIVED, never
  // counted here: `initializeInstructions` lists `visibleRecipes(view)`, so what a
  // caller sees is whatever the catalog holds for its audience. This comment said
  // "the eight recipes" and the catalog had reached 20 (15 of them tenant-visible,
  // read 2026-09-12) — a count in prose is a second enumeration, and it drifts
  // silently because nothing checks it. UNGATED, prose-only,
  // reads nothing and dispatches nothing — the `mcp.help` shape on all three.
  'mcp.docs': {
    rest: na(
      'MCP protocol meta — the recipes are how a MODEL composes the REST/MCP surface, and the same store is mirrored as `resources/*`; a route would be a second publisher',
    ),
    mcp: mcp('docs'),
    ui: na('the human form is the docs site; the dashboard renders screens, not a recipe store'),
  },
  'service.openapi': {
    rest: yes,
    mcp: na(
      'the twin of `mcp.help`, from the other side: each transport publishes its OWN catalog ' +
        'in its own vocabulary (`tools/list` + `help` there, this document here), and a verb ' +
        'serving the REST document would put one catalog inside the other',
    ),
    ui: na(
      'the human form of this catalog is the generated API reference on the docs site, built ' +
        'from the same `REST_ROUTES` by `scripts/api-doc.ts`; a dashboard screen rendering a ' +
        'spec would be a third publisher of one source',
    ),
  },
  // The three OAuth 2.1 discovery documents share one verdict on the two absent
  // surfaces, and it is the same shape as `service.openapi`'s: they describe a
  // transport rather than exercise a capability through one.
  'service.oauth_protected_resource': OAUTH_DISCOVERY,
  'service.oauth_protected_resource_mcp': OAUTH_DISCOVERY,
  'service.oauth_authorization_server': OAUTH_DISCOVERY,
  'service.oauth_register': OAUTH_REGISTRATION,
  'service.oauth_authorize': OAUTH_GRANT,
  'service.oauth_consent': OAUTH_GRANT,
  'service.oauth_token': OAUTH_GRANT,

  // ── identity + the personal plane ─────────────────────────────────────────
  'account.me': { rest: yes, mcp: mcp('account.me'), ui: yes },
  // The account's maintenance posture (plan 102 slice 06, #7545): REST and MCP mount the
  // same control (`apps/api/src/deps/account-posture.ts`). No account-settings page exists
  // yet; the plan-file switch is the precedent for an API/MCP-only account setting.
  // #7785 part 3: the ban lists, both doors over ONE control (`apps/api` `deps/external-bans.ts`).
  // The issue's own trip condition for a dashboard is the first real ignore.
  'account.ban_list': { rest: yes, mcp: mcp('account.ban_list'), ui: gap('API first (#7785)') },
  'account.ban_add': { rest: yes, mcp: mcp('account.ban_add'), ui: gap('API first (#7785)') },
  'account.ban_remove': { rest: yes, mcp: mcp('account.ban_remove'), ui: gap('API first (#7785)') },
  'account.ban_rereviews': {
    rest: yes,
    mcp: mcp('account.ban_rereviews'),
    ui: gap('API first (#7785)'),
  },
  'account.ban_shared_list': {
    rest: yes,
    mcp: mcp('account.ban_shared_list'),
    ui: gap('API first (#7785)'),
  },
  'account.ban_shared_add': {
    rest: yes,
    mcp: mcp('account.ban_shared_add'),
    ui: gap('API first (#7785)'),
  },
  'account.ban_shared_remove': {
    rest: yes,
    mcp: mcp('account.ban_shared_remove'),
    ui: gap('API first (#7785)'),
  },
  'account.posture_get': {
    rest: yes,
    mcp: mcp('account.posture_get'),
    ui: gap('no dashboard account-settings surface for the maintenance posture yet'),
  },
  'account.posture_set': {
    rest: yes,
    mcp: mcp('account.posture_set'),
    ui: gap('no dashboard account-settings surface for the maintenance posture yet'),
  },
  'account.whoami': {
    rest: na(
      '`GET /v1/me` is the REST self-check and answers the same `scopes` (task #74); `whoami` ' +
        'is the ungated MCP handshake probe',
    ),
    mcp: mcp('whoami'),
    ui: na('the SPA resolves the signed-in account from `GET /v1/me`'),
  },
  // The onboarding LADDER — "what stands between this credential and a scout
  // call", composed from reads the other surfaces already serve. It is now
  // SERVED ON ALL THREE, and this row previously said the opposite on two of
  // them: `rest` was `na` ("a REST twin would be a third rendering of one set of
  // facts") and `ui` was `na` ("the dashboard renders the same preconditions as
  // the onboarding rail"). Both were arguments against a second IMPLEMENTATION,
  // which is right, and both were recorded as an absence of a DOOR, which is a
  // different thing. Arc 101 slice 12 step 3 took the other route: one
  // implementation (`resolveReadiness`, `packages/mcp/src/tools.ts`) over one
  // `OnboardingProbe`, with `GET /v1/next-steps` serving its answer unprojected
  // and the dashboard's repo workspace rendering that response — so the three
  // addresses cannot drift, because there is nothing to drift from.
  //
  // It stays a different question from the funnel read `GET /v1/onboarding`,
  // which answers WHERE an account is in the wizard: this answers what still
  // stands between THIS credential (and optionally THIS repo) and a first scout
  // call, per rung, with the fact behind each verdict and where it clears.
  'account.next_steps': {
    rest: yes,
    mcp: mcp('next_steps'),
    ui: yes,
  },
  // "HOW IS IT GOING" (arc 101 slice 12 §A2) — the sibling question to the ladder
  // above, and the one a user asks from a chat client after their first scout
  // call. It COMPOSES reads that are each already served here — the fleet
  // aggregate (`box.utilization`, REST + UI), the stall report (`task.stalls`,
  // REST + MCP), the session usage roll-up (`session.list`), the task
  // conversion tallies (`task.list`) and, since arc 101 slice 09, the pending
  // approvals queue behind the `waiting` block (`pr.pending_approvals`, REST + MCP + UI) —
  // and derives exactly ONE field of its own:
  // `advice`, the purchase verdict `adviseCapacity` reaches from the numbers the
  // capacity blocks already publish.
  //
  // BOTH ABSENCES ARE `gap`, NOT `na`, and the row above is why: its `rest`/`ui`
  // stood as `na` on the argument that a second door would be a second RENDERING
  // of facts already served, which was an argument against a second
  // IMPLEMENTATION and got recorded as a decision about DOORS. The same argument
  // is available here and is refused for the same reason — `resolveStatus` is one
  // function, and a REST twin or a dashboard panel would call it exactly as
  // `GET /v1/next-steps` calls `resolveReadiness`. Neither is built, so neither is
  // claimed.
  'account.status': {
    rest: gap(
      'the composed answer is MCP-only today; the shape a route would serve is `resolveStatus`',
    ),
    mcp: mcp('status'),
    ui: gap(
      'the Fleet page renders the utilization INPUT, never the composed answer or its stall block',
    ),
  },
  'account.active_account_switch': {
    rest: yes,
    mcp: mcp('account.active_account_switch'),
    ui: na(
      'the org lives in the URL (#460) — the switcher NAVIGATES rather than mutating the session',
    ),
  },
  // The `account` verb's `accept_tos` runs the SAME `recordTosConsent` use-case
  // the route does, so an agent-recorded consent stamps the user row and lands
  // one `user.tos_accepted` line. It ALSO seam-audits (`army.account.accept_tos`),
  // unlike `active_account_switch`: "did the human consent, or did their agent?"
  // is the question a terms dispute asks, and the durable row cannot answer it.
  'account.accept_tos': { rest: yes, mcp: mcp('account.accept_tos'), ui: yes },
  // The region question and the separate cross-border consent (#5881): the SAME
  // `recordRegion` / `recordConsentDecision` use-cases behind the route and the
  // verb, each a browser-session act like `accept_tos`, and each seam-audited on
  // MCP for the same reason (did the human declare it, or their agent?).
  'account.region_set': { rest: yes, mcp: mcp('account.region_set'), ui: yes },
  'account.consent_set': { rest: yes, mcp: mcp('account.consent_set'), ui: yes },
  // One human, many logins (#466). The list is a REST read the `/account/logins` page renders;
  // the unlink is a browser-session act on purpose (#1738): the route refuses any
  // credential but a live cookie session, so no MCP caller (a token, always) could
  // reach it — the same reason the link is an IdP round-trip, not a `/v1` call.
  'account.identity_list': {
    rest: yes,
    mcp: gap('the account verb does not list the human’s logins yet'),
    ui: yes,
  },
  'account.identity_unlink': {
    rest: yes,
    mcp: na('unlinking a login is refused to every token; only a signed-in browser session may'),
    ui: yes,
  },
  // Email-proof binding (#7426): the candidates read is the `/account/logins` page's,
  // and the request is a browser-session act for the unlink's reason — the mailed link
  // only confirms from the session that asked, which no token has.
  'account.link_candidates': {
    rest: yes,
    mcp: gap('the account verb does not list link candidates yet'),
    ui: yes,
  },
  'account.link_request': {
    rest: yes,
    mcp: na('the mailed link confirms only from the browser session that asked; a token has none'),
    ui: yes,
  },
  // The PERSONAL plane (epic #635) rides the `account` verb, NOT `billing` — that
  // verb is the ORG pool, a different authorization (`write:billing`, owner/admin)
  // over keys that pay for the whole account's autonomous work. One shared fence
  // across the two is what let an org admin invalidate a member's private key
  // (#1418). Like the `prompt` verb's self layer, every action here resolves the
  // member from the ACTOR — no argument names a user — so a credential with no
  // human behind it is refused before the port.
  'self.keys_list': { rest: yes, mcp: mcp('account.keys_list'), ui: yes },
  'self.key_add': { rest: yes, mcp: mcp('account.key_add'), ui: yes },
  // arc 101 slice 02 — the member-scoped mirror of `billing.provider_models`
  // (`POST /v1/me/keys/models`). It exists because `write:billing` is ADMIN+, so
  // a plain member capturing a PERSONAL key cannot spend the org twin.
  'self.provider_models': {
    rest: yes,
    // Step 6 closed the MCP half: `account provider_models` lists a provider's
    // real model ids for a raw key and `account key_add` takes the pick plus a
    // tier tag per model, so a chat-driven capture and the wizard produce ONE
    // shape. Both doors run the same `enumerateProviderModels` composition, so
    // neither can skip the per-account budget or reorder the SSRF + wire gates.
    mcp: mcp('account.provider_models'),
    // `<AddKeyModal/>` on `/account/my-keys` probes THIS route rather than the
    // org twin, which is the whole reason the mirror exists.
    ui: yes,
  },
  'self.key_verify': { rest: yes, mcp: mcp('account.key_verify'), ui: yes },
  'self.key_rotate': { rest: yes, mcp: mcp('account.key_rotate'), ui: yes },
  'self.key_delete': { rest: yes, mcp: mcp('account.key_delete'), ui: yes },
  // #1247: documented edit path for the personal pool's `kind`. Same MCP verb
  // as the REST twin (`PATCH /v1/me/keys/:id/kind`); the column comment on
  // `byok_keys.kind` once claimed the route existed and didn't — this is what
  // makes the comment true.
  'self.key_kind_set': { rest: yes, mcp: mcp('account.key_kind_set'), ui: yes },
  // plan 102 slice 05b: order the caller's personal pool (`byok_keys.priority`).
  'self.key_priority_set': { rest: yes, mcp: mcp('account.key_priority_set'), ui: yes },
  // Capacity-aware scheduling: declare the caller's personal key's concurrency.
  'self.key_capacity_set': {
    rest: yes,
    mcp: mcp('account.key_capacity_set'),
    ui: gap('the dashboard key card has no capacity field; a member declares it over REST or MCP'),
  },
  'self.llm_pref_get': { rest: yes, mcp: mcp('account.llm_pref_get'), ui: yes },
  'self.llm_pref_set': { rest: yes, mcp: mcp('account.llm_pref_set'), ui: yes },
  'self.llm_pref_clear': { rest: yes, mcp: mcp('account.llm_pref_clear'), ui: yes },
  // ── the personal GitHub token: the user-token WORK lane (arc 101 slice 05) ──
  //
  // GRADED, plan 101 slice 04: all three read "the personal GitHub-token lane has
  // no verb" — a restatement of the absence, not a reason for it, and the three
  // legs do not share one. The CONNECT is closed; the other two are open.
  'self.github_token_get': {
    rest: yes,
    // Not refused by doctrine: the answer is metadata (connected, expiry, the repo
    // allowlist) and never the token. It is unbuilt because it has no consumer
    // yet — nothing on this surface reads or names a repo's `user_token` binding
    // (`repo_bindings.mode`), so a verb here would be a consumer-less tool, which
    // `.claude/agents/tool-implementer.md` refuses. Owed the day one does: an
    // expired PAT stops that repo's work, and an agent that cannot read the expiry
    // cannot say why.
    mcp: gap('no MCP reader of the `user_token` repo binding yet, so nothing consumes it'),
    ui: yes,
  },
  'self.github_token_set': {
    rest: yes,
    // CLOSED. The body is a caller-supplied credential, which is the line this
    // surface does not cross (`integration.connect`, `sso set`'s `client_secret`),
    // and the token is stronger than either: work on a user-token repo posts as the
    // USER's own GitHub account with no bot footer, so it IS their identity. The
    // connect is a live-probed ceremony that must resolve the presented token to
    // the CONNECTING user's own account and refuse a classic PAT, an unexpiring
    // one, or one that cannot reach a declared repo — and the human carries
    // GitHub's one-account-per-person terms for what it then does. That consent is
    // a person's to give at `/account/github`, not an agent's to pass through.
    mcp: na(
      'the body is a caller-supplied credential — and this one is the user’s own GitHub identity, ' +
        'since work on a user-token repo posts as them with no bot footer. It is connected by the ' +
        'human whose account it acts as, at `/account/github` or `POST /v1/me/github-token`',
    ),
    ui: yes,
  },
  'self.github_token_clear': {
    rest: yes,
    // Open like the read, and for the same reason: disconnecting moves no secret,
    // it drops a row. Unbuilt because nothing on this surface reads the lane it
    // ends, so an agent has no way to know there is one to disconnect.
    mcp: gap('no MCP reader of the `user_token` repo binding yet, so nothing consumes it'),
    ui: yes,
  },
  // ── the user-token repo bindings (arc 101 slice 05) ──────────────────────
  'self.repo_binding_list': {
    rest: yes,
    mcp: gap(
      'no MCP read of the caller’s own user-token bindings yet; `project list` shows the repos',
    ),
    ui: yes,
  },
  'self.repo_binding_set': {
    rest: yes,
    // The MCP twin is `project add` with `via: 'user_token'` — ONE executor
    // (`apps/api/src/deps/user-token-binding.ts`). Not claimed here as an address
    // because `project.add` is already claimed by its own row (rows-work.ts), and
    // invariant 2 of capability-manifest.test.ts allows exactly one claimant.
    mcp: gap(
      'served as `project add` with `via: "user_token"`, whose manifest row is `project.add`',
    ),
    ui: yes,
  },
  'self.repo_binding_clear': {
    rest: yes,
    mcp: gap('no MCP unbind verb yet; unbind at `/account/github` or over REST'),
    ui: yes,
  },
  // The `prompt` verb resolves the personal layer from the ACTOR, never from an
  // argument — so, exactly like these routes, it reaches only the caller's own
  // prose and refuses a credential with no user behind it.
  'self.system_prompt_get': { rest: yes, mcp: mcp('prompt.self_get'), ui: yes },
  'self.system_prompt_set': { rest: yes, mcp: mcp('prompt.self_set'), ui: yes },
  'self.system_prompt_clear': { rest: yes, mcp: mcp('prompt.self_clear'), ui: yes },

  // ── org members + SSO ─────────────────────────────────────────────────────
  'member.list': { rest: yes, mcp: mcp('member.list'), ui: yes },
  'member.invite': { rest: yes, mcp: mcp('member.invite'), ui: yes },
  'member.set_role': { rest: yes, mcp: mcp('member.set_role'), ui: yes },
  'member.remove': { rest: yes, mcp: mcp('member.remove'), ui: yes },
  // Pending-invitations panel (task #19): this org's `org_member` invitations,
  // every status, and withdrawing one PENDING invitation.
  'member.list_invitations': { rest: yes, mcp: mcp('member.list_invitations'), ui: yes },
  'member.revoke_invitation': { rest: yes, mcp: mcp('member.revoke_invitation'), ui: yes },
  'org_sso.get': { rest: yes, mcp: mcp('sso.get'), ui: yes },
  // `sso.set` reaches the non-secret half only: the MCP verb carries no
  // `client_secret` (a caller-SUPPLIED credential never crosses that boundary —
  // the same rule that leaves `integration` without a `connect`), so it updates
  // an existing connection and reuses the stored sealed secret, refusing when
  // there is none to keep. Served, per this file's "one capability, not one
  // field" rule — the `project.update` precedent above.
  'org_sso.set': { rest: yes, mcp: mcp('sso.set'), ui: yes },
  'org_sso.delete': { rest: yes, mcp: mcp('sso.delete'), ui: yes },

  // ── platform tokens ───────────────────────────────────────────────────────
  'token.list': { rest: yes, mcp: mcp('token.list'), ui: yes },
  'token.mint': { rest: yes, mcp: mcp('token.mint'), ui: yes },
  'token.rename': { rest: yes, mcp: mcp('token.rename'), ui: yes },
  'token.revoke': { rest: yes, mcp: mcp('token.revoke'), ui: yes },

  // Platform-OPERATOR credential issuance (#2055) — `POST /v1/admin/operator-tokens`,
  // the ONE door to the five `admin:*` writes. Sibling of the four rows above and
  // deliberately not one of them: `token.mint` refuses every scope this one exists
  // to issue.
  // The audited agent mint (`apps/api/src/deps/agent-token-mint.ts`).
  'agent_token.mint': {
    rest: yes,
    mcp: na(
      "it returns a plaintext credential in another person's name, and every MCP call happens inside an agent session whose transcript is durably persisted. An agent asks for one over `bun run infra agent-token`, which leaves it in that operator's hands.",
    ),
    ui: na('an operator and agent door: `bun run infra agent-token` is its easy path'),
  },
  'operator_token.issue': {
    rest: yes,
    mcp: na(
      'NOT because a session-only verb is unbuildable — `McpCaller` carries `sessionId` and ' +
        '`account active_account_switch` already refuses without one (`no_session`), so this ' +
        "door's anti-renewal gate would port across exactly. The refusal is about WHAT it " +
        'returns: a one-hour plaintext credential carrying the five cross-tenant staff writes. ' +
        'Every MCP call happens inside an agent session whose transcript is durably persisted ' +
        'and readable across tenants by `admin:read:runs` — the single most disclosing store on ' +
        'the platform — so the verb would write the widest credential we mint into it. ' +
        '`token mint` returns a plaintext PAT over MCP and is right to: that one is ' +
        "account-scoped and the caller's own. This one is neither.",
    ),
    ui: gap(
      'no console door yet — an operator issues one with a cookie and curl. `gap` and not ' +
        '`na` on purpose: the route refuses every credential that is not a live cookie session, ' +
        'so a browser is ALREADY the only thing that can reach it and a button adds no ' +
        'reachability it does not have. What it would add is the deliberate, named, expiring ' +
        'act the design is built around, in the one surface that can already perform it.',
    ),
  },
};
